// File Name: services/contactService.js
import { 
  collection, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Contact Service for handling contact form submissions
 */
class ContactService {
  
  /**
   * Submits a contact message to Firestore
   * @param {ContactMessage} contactData - The contact form data
   * @returns {Promise<string>} - Document ID of the created record
   */
  async submitContactMessage(contactData) {
    try {
      // Validate required fields
      if (!contactData.name || !contactData.email || !contactData.message) {
        throw new Error('Name, email, and message are required fields');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactData.email)) {
        throw new Error('Please provide a valid email address');
      }

      // Prepare the data with timestamp
      const messageData = {
        name: contactData.name.trim(),
        emirate: contactData.emirate || 'Not specified',
        email: contactData.email.trim().toLowerCase(),
        phoneNumber: contactData.phoneNumber || 'Not provided',
        message: contactData.message.trim(),
        timestamp: serverTimestamp(),
        status: 'new'
      };

      // Add to Firestore
      const docRef = await addDoc(collection(db, 'contactMessages'), messageData);
      
      console.log('Contact message submitted successfully with ID:', docRef.id);
      return docRef.id;

    } catch (error) {
      console.error('Error submitting contact message:', error);
      throw new Error(`Failed to submit contact message: ${error.message}`);
    }
  }

  /**
   * Validates contact form data
   * @param {ContactMessage} data - The contact form data to validate
   * @returns {Object} - Validation result { isValid: boolean, errors: string[] }
   */
  validateContactData(data) {
    const errors = [];

    if (!data.name || data.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }

    if (!data.email) {
      errors.push('Email is required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.push('Please provide a valid email address');
      }
    }

    if (!data.message || data.message.trim().length < 10) {
      errors.push('Message must be at least 10 characters long');
    }

    if (data.phoneNumber && data.phoneNumber.trim() !== '') {
      const phoneRegex = /^[+]?[0-9\s\-\(\)]{8,20}$/;
      if (!phoneRegex.test(data.phoneNumber)) {
        errors.push('Please provide a valid phone number');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export a singleton instance
export const contactService = new ContactService();
export default contactService;